import PropTypes from "prop-types";
import { useState } from "react";
import { CalendarX, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "@/components/ui/empty";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import { EM_DASH } from "routes/custom_tables/grid/cellHelpers";
import { RecordLink } from "routes/swoogo/overviewPrimitives";
import ConfirmRemoveSessionRegistrantsDialog from "routes/swoogo/ConfirmRemoveSessionRegistrantsDialog";

const RegistrantSessionsCard = ({ registrant }) => {
  const canRemoveSessionRegistrants = useAuthorized([
    ACL_RELATIONSHIPS.swoogo.removeSessionRegistrants,
  ]);
  const [sessionIdToRemove, setSessionIdToRemove] = useState(null);
  const sessions = registrant.sessions ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <CalendarX />
                </EmptyMedia>
                <EmptyDescription>
                  Not registered for any session.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
              {sessions.map(session => (
                <li
                  key={session.id}
                  className="tw:flex tw:items-center tw:justify-between tw:gap-4"
                >
                  <span className="tw:min-w-0 tw:break-words">
                    <RecordLink to={`/swoogo/sessions/${session.id}`}>
                      {session.name}
                    </RecordLink>
                  </span>
                  <span className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:text-muted-foreground">
                    {session.date ?? EM_DASH}
                    {canRemoveSessionRegistrants ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove from ${session.name}`}
                        onClick={() => setSessionIdToRemove(session.id)}
                      >
                        <Trash2 />
                      </Button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      {sessionIdToRemove != null && (
        <ConfirmRemoveSessionRegistrantsDialog
          registrants={[registrant]}
          sessionId={sessionIdToRemove}
          onOpenChange={() => setSessionIdToRemove(null)}
        />
      )}
    </>
  );
};

RegistrantSessionsCard.propTypes = {
  registrant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    email: PropTypes.string,
    sessions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        date: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default RegistrantSessionsCard;
